import { AppBreakpoint } from '@makyoapp/makyo-sdk/models/AppBuilder'

import { MT_BREAKPOINTS } from '@/shared/brand-kit'
import { viewportMaxWidthMap } from '@/shared/main-navigation-bar'

import { AccordionDesignSettings } from './components/accordion.design'
import { AlertDesignSettings } from './components/alert.design'
import { ArrayGalleryDesignSettings } from './components/array-gallery.design'
import { AudioDesignSettings } from './components/audio.design'
import { BarChartDesignSettings } from './components/bar-chart.design'
import { ButtonDesignSettings } from './components/button.design'
import { CalendarDesignSettings } from './components/calendar.design'
import { CardDesignSettings } from './components/card.design'
import { CircularProgressDesignSettings } from './components/circular-progress.design'
import { CommentDesignSettings } from './components/comment.design'
import { DividerDesignSettings } from './components/divider.design'
import { EventDesignSettings } from './components/event.design'
import { FabDesignSettings } from './components/fab.design'
import { FileUploadDesignSettings } from './components/file-upload.design'
import { FormDesignSettings } from './components/form.design'
import { GalleryDesignSettings } from './components/gallery.design'
import { IconDesignSettings } from './components/icon-button.design'
import { ImageUploadDesignSettings } from './components/image-upload.design'
import { ImageDesignSettings } from './components/image.design'
import { LinearProgressDesignSettings } from './components/linear-progress.design'
import { ListDesignSettings } from './components/list.design'
import { LocationDesignSettings } from './components/location.design'
import { LoginFormDesignSettings } from './components/login-form.design'
import { NewsletterDesignSettings } from './components/newsletter.design'
import { PieCharDesignSettings } from './components/pie-chart.design'
import { PricingDesignSettings } from './components/pricing.design'
import { PromptDesignSettings } from './components/prompt.design'
import { RadioDesignSettings } from './components/radio.design'
import { RatingDesignSettings } from './components/rating.design'
import { RichTextDesignSettings } from './components/richtext.design'
import { SectionDesignSettings } from './components/section.design'
import { ShoppingCartDesignSettings } from './components/shopping-cart.design'
import { SingleRowDesignSettings } from './components/single-row.design'
import { TableDesignSettings } from './components/table.design'
import { TabsDesignSettings } from './components/tabs.design'
import { TextDesignSettings } from './components/text.design'
import { TimelineDesignSettings } from './components/timeline.design'
import { VerticalMenuDesignSettings } from './components/vertical-menu.design'
import { VideoDesignSettings } from './components/video.design'

const DefaultResponsiveSettings: Record<string, any> = {
  ...TextDesignSettings,
  ...ButtonDesignSettings,
  ...FabDesignSettings,
  ...SectionDesignSettings,
  ...RichTextDesignSettings,
  ...FormDesignSettings,
  ...IconDesignSettings,
  ...TabsDesignSettings,
  ...ImageDesignSettings,
  ...VideoDesignSettings,
  ...AudioDesignSettings,
  ...SingleRowDesignSettings,
  ...TableDesignSettings,
  ...LinearProgressDesignSettings,
  ...CircularProgressDesignSettings,
  ...LocationDesignSettings,
  ...PieCharDesignSettings,
  ...BarChartDesignSettings,
  ...ListDesignSettings,
  ...CardDesignSettings,
  ...AccordionDesignSettings,
  ...EventDesignSettings,
  ...GalleryDesignSettings,
  ...ArrayGalleryDesignSettings,
  ...RadioDesignSettings,
  ...FileUploadDesignSettings,
  ...ImageUploadDesignSettings,
  ...LoginFormDesignSettings,
  ...DividerDesignSettings,
  ...RatingDesignSettings,
  ...VerticalMenuDesignSettings,
  ...ShoppingCartDesignSettings,
  ...CommentDesignSettings,
  ...TimelineDesignSettings,
  ...CalendarDesignSettings,
  ...PromptDesignSettings,
  ...PricingDesignSettings,
  ...NewsletterDesignSettings,
  ...AlertDesignSettings,
}

// Color adaptive settings must have light and dark keys
// const DefaultColorAdaptiveSettings: Record<string, any> = {
//   ...ButtonDesignSettings,
//   ...FabDesignSettings,
//   ...IconDesignSettings,
//   ...LocationDesignSettings,
//   ...CardDesignSettings,
//   ...TextDesignSettings,
//   ...DividerDesignSettings,
//   ...VerticalMenuDesignSettings,
//   ...RatingDesignSettings,
//   ...CommentDesignSettings,
//   ...CircularProgressDesignSettings,
//   ...LinearProgressDesignSettings,
//   ...AccordionDesignSettings,
//   ...ListDesignSettings,
// }

export function hasResponsiveSettings(settingKey: string) {
  return !!DefaultResponsiveSettings[settingKey] && !hasColorAdaptiveSettings(settingKey)
}

export function hasColorAdaptiveSettings(settingKey: string) {
  return !!DefaultResponsiveSettings[settingKey]?.light && !!DefaultResponsiveSettings[settingKey]?.dark
}

export function getDefaultColorAdaptiveSettings(settingKey: string) {
  if (typeof DefaultResponsiveSettings[settingKey] === 'undefined') return

  const defaultSettings = DefaultResponsiveSettings[settingKey]

  if (defaultSettings?.dark === undefined || defaultSettings?.light === undefined) return
  return defaultSettings
}

export function getDefaultResponsiveSettings(settingKey: string) {
  if (typeof DefaultResponsiveSettings[settingKey] === 'undefined') return

  return DefaultResponsiveSettings[settingKey]
}

function getDefaultColorValue(settingKey: string, mode: 'light' | 'dark') {
  const defaults = getDefaultColorAdaptiveSettings(settingKey)
  if (!defaults) return undefined
  if (typeof defaults !== 'object') return defaults

  const oppositeMode = mode === 'light' ? 'dark' : 'light'
  return defaults?.[mode] ?? defaults?.[oppositeMode]
}

export function normalizeColorAdaptiveValue(settingKey: string, value: any) {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const lightValue = value.light ?? value.dark ?? getDefaultColorValue(settingKey, 'light')
    const darkValue = value.dark ?? value.light ?? getDefaultColorValue(settingKey, 'dark')

    return {
      light: lightValue,
      dark: darkValue,
    }
  }

  if (value !== undefined) {
    return {
      light: value,
      dark: value,
    }
  }

  return {
    light: getDefaultColorValue(settingKey, 'light'),
    dark: getDefaultColorValue(settingKey, 'dark'),
  }
}

function clampValueByDeviceMaxSize(value: string | number, device: AppBreakpoint) {
  const maxPixel = viewportMaxWidthMap[device]
  const theValue = Array.isArray(value) ? value[0] : value
  if (typeof theValue === 'string' && theValue.includes('px')) {
    const numericValue = parseFloat(theValue.replace('px', ''))
    return numericValue > maxPixel ? `${maxPixel}px` : theValue
  }

  return theValue
}

export function formatResponsiveClampedValue(settings: Record<string, any>, updatedValue: any) {
  if (!!settings) {
    if (typeof updatedValue === 'boolean') {
      return {
        desktop: updatedValue,
        phone: updatedValue,
        'phone-landscape': updatedValue,
        tablet: updatedValue,
        'tablet-landscape': updatedValue,
        tv: updatedValue,
      }
    }

    return {
      desktop: [clampValueByDeviceMaxSize(updatedValue, 'desktop')],
      phone: [clampValueByDeviceMaxSize(updatedValue, 'phone')],
      'phone-landscape': [clampValueByDeviceMaxSize(updatedValue, 'phone-landscape')],
      tablet: [clampValueByDeviceMaxSize(updatedValue, 'tablet')],
      'tablet-landscape': [clampValueByDeviceMaxSize(updatedValue, 'tablet-landscape')],
      tv: [clampValueByDeviceMaxSize(updatedValue, 'tv')],
    }
  }

  return updatedValue
}

export function formatResponsiveSettingValues(settings: Record<string, any> = {}, updatedValue: any) {
  // check if its has direct responsive keys
  if (!!settings && !!Object.keys(settings).some((k) => k === 'phone')) {
    return {
      desktop: updatedValue,
      phone: updatedValue,
      'phone-landscape': updatedValue,
      tablet: updatedValue,
      'tablet-landscape': updatedValue,
      tv: updatedValue,
    }
  }

  // check if typography group
  const isTypographyGroup =
    typeof settings === 'object' && Object.keys(settings).some((key) => key.includes('typography'))

  if (isTypographyGroup) {
    let formatted = Object.entries(settings).reduce((acc, [key, value]) => {
      return {
        ...acc,
        [key]: Object.entries(value).reduce((acc, [deviceKey, val]) => {
          return {
            ...acc,
            [deviceKey]: {
              typography: updatedValue?.[key]?.typography || (val as any)?.typography,
            },
          }
        }, {}),
      }
    }, {})
    return formatted
  }

  return typeof updatedValue === 'boolean' ? Boolean(updatedValue) : updatedValue
}

export function formatResponsiveValueByDevice(
  settingKey: string,
  settings: Record<string, any>,
  updatedValue: any,
  device: AppBreakpoint,
  isOverlay: boolean = false,
  isDetailPage: boolean = false
) {
  const currentSettings = (
    typeof settings !== 'undefined' ? settings : DefaultResponsiveSettings[settingKey]
  ) as Record<string, any>

  if (isOverlay && currentSettings?.overflowSetting) {
    const overlaySettings = {
      ...currentSettings,
      ...currentSettings.overflowSetting,
    }

    return {
      ...overlaySettings,
      [device]: updatedValue,
    }
  }

  if (isDetailPage && currentSettings?.detailPageSetting) {
    const detailPageSettings = {
      ...currentSettings,
      ...currentSettings.detailPageSetting,
    }

    return {
      ...detailPageSettings,
      [device]: updatedValue,
    }
  }

  // check if typography group
  const isTypographyGroup =
    typeof currentSettings === 'object' && Object.keys(currentSettings).some((key) => key.includes('typography'))

  if (isTypographyGroup) {
    const allKeys = new Set([...Object.keys(currentSettings), ...Object.keys(updatedValue || {})])

    const allDevices = MT_BREAKPOINTS.map((bp) => bp.value)

    let formatted = Array.from(allKeys).reduce((acc, key) => {
      const currentValue = currentSettings[key] || {}
      const updatedValueForKey = updatedValue?.[key]

      return {
        ...acc,
        [key]: allDevices.reduce((acc, deviceKey) => {
          const currentDeviceValue = currentValue[deviceKey]
          return {
            ...acc,
            [deviceKey]: deviceKey === device ? updatedValueForKey : currentDeviceValue,
          }
        }, {}),
      }
    }, {})
    return formatted
  }

  // return updated responsive values
  return {
    ...(currentSettings || {}),
    [device]: updatedValue,
  }
}

export function formatColorAdaptiveValue(
  settingKey: string,
  settings: Record<string, any>,
  updatedValue: any,
  mode: 'light' | 'dark'
) {
  const currentSettings = normalizeColorAdaptiveValue(settingKey, settings)

  if (updatedValue && typeof updatedValue === 'object' && !Array.isArray(updatedValue)) {
    const normalizedUpdated = normalizeColorAdaptiveValue(settingKey, updatedValue)
    return {
      light: normalizedUpdated.light ?? currentSettings.light,
      dark: normalizedUpdated.dark ?? currentSettings.dark,
    }
  }

  if (typeof updatedValue === 'undefined') {
    return currentSettings
  }

  return {
    ...currentSettings,
    [mode]: updatedValue,
  }
}
